import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Warehouse, Trash2, ArrowDownCircle, ArrowUpCircle, RefreshCw, Edit2, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CellaMovimenti = () => {
  const [movimenti, setMovimenti] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('all');
  const [editingMovimento, setEditingMovimento] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [movRes, prodRes, fornRes] = await Promise.all([
        axios.get(`${API}/cella/movimenti`).catch(e => ({ data: [] })),
        axios.get(`${API}/prodotti`).catch(e => ({ data: [] })),
        axios.get(`${API}/fornitori`).catch(e => ({ data: [] }))
      ]);
      setMovimenti(movRes.data || []);
      setProdotti(prodRes.data || []);
      setFornitori(fornRes.data || []);
    } catch (error) {
      console.error('Errore caricamento movimenti cella:', error);
      toast.error('Errore caricamento movimenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (movimentoId) => {
    if (!window.confirm('Eliminare questo movimento? La situazione cella verrà aggiornata.')) return;
    
    try {
      await axios.delete(`${API}/cella/movimenti/${movimentoId}`);
      toast.success('Movimento eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const openEditModal = (movimento) => {
    setEditingMovimento(movimento);
    setEditForm({
      prodotto_sigla: movimento.prodotto_sigla || '',
      prodotto_nome: movimento.prodotto_nome || '',
      fornitore: movimento.fornitore || '',
      kg_movimento: movimento.kg_movimento || 0,
      note: movimento.note || ''
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/cella/movimenti/${editingMovimento.id}`, {
        ...editForm,
        kg_movimento: parseFloat(editForm.kg_movimento)
      });
      toast.success('Movimento aggiornato. Situazione cella aggiornata.');
      setEditingMovimento(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const filteredMovimenti = filtroTipo === 'all' 
    ? movimenti 
    : movimenti.filter(m => m.tipo_movimento === filtroTipo);

  const getTipoLabel = (tipo) => {
    const labels = {
      'ingresso': 'Ingresso',
      'uscita_parziale': 'Uscita Parz.',
      'uscita_totale': 'Uscita Tot.'
    };
    return labels[tipo] || tipo;
  };

  const getTipoColor = (tipo) => {
    if (tipo === 'ingresso') return 'bg-[#22C55E]/20 text-[#22C55E]';
    return 'bg-[#EF4444]/20 text-[#EF4444]';
  };

  const getTipoIcon = (tipo) => {
    if (tipo === 'ingresso') return <ArrowDownCircle className="w-4 h-4" />;
    return <ArrowUpCircle className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#8B5CF6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cella-movimenti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#8B5CF6]/20 rounded-xl">
            <Warehouse className="w-6 h-6 text-[#8B5CF6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white uppercase tracking-tight">Movimenti Cella</h1>
            <p className="text-sm text-white/50">Storico ingressi e uscite</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
            data-testid="filtro-tipo-select"
          >
            <option value="all" className="bg-[#0F172A]">Tutti</option>
            <option value="ingresso" className="bg-[#0F172A]">Solo Ingressi</option>
            <option value="uscita_parziale" className="bg-[#0F172A]">Solo Uscite Parz.</option>
            <option value="uscita_totale" className="bg-[#0F172A]">Solo Uscite Tot.</option>
          </select>
          <button
            onClick={fetchData}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            title="Aggiorna"
          >
            <RefreshCw className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-[#22C55E]">
            {movimenti.filter(m => m.tipo_movimento === 'ingresso').length}
          </p>
          <p className="text-xs text-white/50 uppercase">Ingressi</p>
        </div>
        <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-[#EF4444]">
            {movimenti.filter(m => m.tipo_movimento.includes('uscita')).length}
          </p>
          <p className="text-xs text-white/50 uppercase">Uscite</p>
        </div>
        <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-[#8B5CF6]">{movimenti.length}</p>
          <p className="text-xs text-white/50 uppercase">Totali</p>
        </div>
      </div>

      {/* Lista Movimenti */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="px-4 py-3 text-left text-xs font-bold text-[#FFE500] uppercase">Data</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-[#FFE500] uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-[#FFE500] uppercase">Prodotto</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-[#FFE500] uppercase">Fornitore</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-[#FFE500] uppercase">KG</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-[#FFE500] uppercase">Residuo</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-[#FFE500] uppercase">Note</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-[#FFE500] uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovimenti.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-white/50">
                    Nessun movimento trovato
                  </td>
                </tr>
              ) : (
                filteredMovimenti.map((mov) => (
                  <tr key={mov.id || mov.timestamp} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3 text-white/70 text-sm">
                      {mov.timestamp ? format(new Date(mov.timestamp), 'dd/MM/yy HH:mm') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTipoColor(mov.tipo_movimento)}`}>
                        {getTipoIcon(mov.tipo_movimento)}
                        {getTipoLabel(mov.tipo_movimento)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded font-mono font-bold text-xs">
                          {mov.prodotto_sigla}
                        </span>
                        <span className="text-white/70 text-sm">{mov.prodotto_nome}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/70">{mov.fornitore}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${mov.tipo_movimento === 'ingresso' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                        {mov.tipo_movimento === 'ingresso' ? '+' : '-'}{mov.kg_movimento} kg
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-white/50">
                      {mov.kg_residuo > 0 ? `${mov.kg_residuo} kg` : '-'}
                    </td>
                    <td className="px-4 py-3 text-white/50 text-sm max-w-[200px] truncate">
                      {mov.note || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEditModal(mov)}
                          className="p-2 bg-[#8B5CF6]/20 hover:bg-[#8B5CF6]/30 rounded-lg transition-colors"
                          title="Modifica"
                          data-testid={`edit-mov-${mov.id}`}
                        >
                          <Edit2 className="w-4 h-4 text-[#8B5CF6]" />
                        </button>
                        <button
                          onClick={() => handleDelete(mov.id)}
                          className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                          title="Elimina"
                          data-testid={`delete-mov-${mov.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Modifica */}
      {editingMovimento && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0F172A] border border-white/20 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-[#8B5CF6]" />
                Modifica Movimento
              </h3>
              <button
                onClick={() => setEditingMovimento(null)}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5 text-white/70" />
              </button>
            </div>

            <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-lg p-3 mb-4">
              <p className="text-sm text-white/70">
                Tipo: <span className="font-medium text-white">{getTipoLabel(editingMovimento.tipo_movimento)}</span>
              </p>
              <p className="text-xs text-white/50 mt-1">
                La modifica dei KG aggiornerà automaticamente la situazione cella
              </p>
            </div>
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Prodotto</label>
                <select
                  value={editForm.prodotto_sigla}
                  onChange={(e) => {
                    const prod = prodotti.find(p => p.sigla === e.target.value);
                    setEditForm({ 
                      ...editForm, 
                      prodotto_sigla: e.target.value,
                      prodotto_nome: prod?.nome || e.target.value
                    });
                  }}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                >
                  {prodotti.map((p) => (
                    <option key={p.sigla} value={p.sigla} className="bg-[#0F172A]">
                      {p.sigla} - {p.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Fornitore</label>
                <select
                  value={editForm.fornitore}
                  onChange={(e) => setEditForm({ ...editForm, fornitore: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                >
                  {fornitori.map((f) => (
                    <option key={f.nome} value={f.nome} className="bg-[#0F172A]">{f.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">KG Movimento</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.kg_movimento}
                  onChange={(e) => setEditForm({ ...editForm, kg_movimento: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Note</label>
                <textarea
                  value={editForm.note}
                  onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white resize-none"
                  rows={2}
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingMovimento(null)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#8B5CF6] text-white rounded-lg font-medium hover:bg-[#8B5CF6]/80 flex items-center justify-center gap-2"
                  data-testid="save-edit-btn"
                >
                  <Save className="w-4 h-4" />
                  Salva
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CellaMovimenti;
